/**
 * useEngine — Engine Lifecycle Hook
 *
 * Mounts the IMS 3D engine into a container <div> via a ref.
 * The engine manages its own R3F canvas inside the container.
 *
 * Lifecycle:
 *   1. Wait for containerRef.current + config to be non-null
 *   2. Call resolvedConfigToEngineConfig() to derive EngineConfig
 *   3. Call createEngine() (async) — mounts R3F canvas into container
 *   4. Subscribe to loadingProgress, sceneLoaded, error events
 *   5. On unmount: call engine.dispose() — unmounts canvas, frees GPU
 *
 * Event → Store mapping:
 *   loadingProgress → setLoadingProgress(progress)
 *   sceneLoaded     → setLoadingProgress(100)
 *   error           → setError({ code, recoverable })
 *
 * Reactive store → engine bridges are handled by dedicated hooks in Viewer.tsx:
 *   useColor  — selectedColorId → engine.setMaterial() (animated, post-init changes)
 *   useRim    — selectedRimId   → engine.showAsset() (hard-swap, post-init changes)
 *   Viewer    — cameraMode      → engine.showPano() / engine.hidePano()
 *
 * @see packages/engine/src/api/createEngine.tsx
 * @see packages/engine/src/api/configAdapter.ts
 * @see docs/ims-brand-frontends/04-engine-integration.md
 */
import { useEffect, useRef } from 'react';
import { createEngine, resolvedConfigToEngineConfig, applyScene } from '@ims/engine';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';
import { detectLayout } from '../utils/detectLayout';

/** Non-recoverable engine errors (device capability failures). */
const FATAL_ENGINE_ERRORS = new Set(['WEBGL_NOT_SUPPORTED', 'MEMORY_EXCEEDED']);

export function useEngine(
  containerRef: React.RefObject<HTMLDivElement | null>,
  config: ResolvedConfig | null,
) {
  const engineRef = useRef<Engine | null>(null);
  const setLoadingProgress = useAppStore((s) => s.setLoadingProgress);
  const setSceneReady = useAppStore((s) => s.setSceneReady);
  const setError = useAppStore((s) => s.setError);
  const setActiveScene = useAppStore((s) => s.setActiveScene);
  const setSelectedColor = useAppStore((s) => s.setSelectedColor);
  const setSelectedRim = useAppStore((s) => s.setSelectedRim);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !config) return;

    let disposed = false;
    const unsubs: Array<() => void> = [];

    const engineConfig = resolvedConfigToEngineConfig(config, container, {
      hdriBasePath: '/app/hdri',
    });

    // On mobile, halve the shadow map resolution (GPU memory constraint, not a style choice).
    // viewerSettings are left exactly as the brand config specifies — no hidden preset switching.
    if (detectLayout() === 'mobile') {
      engineConfig.shadowMapSize = 1024;
    }

    createEngine(engineConfig)
      .then((engine) => {
        if (disposed) {
          engine.dispose();
          return;
        }
        engineRef.current = engine;

        // Simulated easing progress — moves monotonically from 5% towards 90%
        // while real engine progress catches up. Prevents the bar from jumping
        // backwards or stalling at 0 during early GLB + HDR loading.
        // Using a min-step of 2 so the bar moves visibly even at high values.
        let simProgress = 5;
        setLoadingProgress(5);
        const simInterval = setInterval(() => {
          simProgress = Math.min(90, simProgress + Math.max(2, (90 - simProgress) * 0.08));
          setLoadingProgress(Math.round(simProgress));
        }, 80);

        unsubs.push(
          engine.on('loadingProgress', ({ progress }) => {
            // Let real progress lead if it's ahead of the simulation
            if (progress > simProgress) {
              simProgress = progress;
              setLoadingProgress(progress);
            }
          }),
          engine.on('sceneLoaded', () => {
            clearInterval(simInterval);
            // Check for ?scene= URL param override (e.g. ?scene=transparent for embed)
            const sceneParam = new URLSearchParams(window.location.search).get('scene');
            const defaultSceneId = config.model.defaultScene ?? '';
            if (sceneParam === 'transparent') {
              // Transparent is a built-in engine preset — no model.json entry needed
              engine.setPreset('transparent');
              setActiveScene('transparent');
            } else {
              // Apply the default scene (lighting, IBL, shadow) now that assets are loaded
              const defaultScene = config.model.scenes?.[defaultSceneId];
              if (defaultScene) {
                applyScene(engine, defaultScene);
              }
              setActiveScene(defaultSceneId);
            }
            // Apply the first exterior color immediately — no animation so the
            // car appears pre-painted when the scene fades in. The MaterialManager
            // must have been initialized with materialTargets (via configAdapter)
            // for setMaterial() to take effect.
            if (config.features.exteriorColors && config.colors.length > 0) {
              const firstColor = config.colors[0];
              firstColor.primary.materials.forEach((mat) => {
                engine.setMaterial(mat, {
                  preset: 'car-paint',
                  colorCode: firstColor.primary.colorCode,
                  type: firstColor.primary.type,
                  animated: false,
                });
              });
              if (firstColor.secondary) {
                firstColor.secondary.materials.forEach((mat) => {
                  engine.setMaterial(mat, {
                    preset: 'car-paint',
                    colorCode: firstColor.secondary!.colorCode,
                    type: firstColor.secondary!.type,
                    animated: false,
                  });
                });
              }
              setSelectedColor(firstColor.id);
            }
            // Preload HDR files for all non-default scenes in the background
            // so switching scenes later is instant (no on-demand load stall).
            const scenes = config.model.scenes;
            if (scenes) {
              const hdrEntries = Object.entries(scenes)
                .filter(([id, scene]) => id !== defaultSceneId && scene.ibl)
                .map(([, scene]) => ({
                  id: scene.ibl!,
                  url: `/app/hdri/${scene.ibl}`,
                  type: 'hdr' as const,
                }));
              // Delay background HDR preloading by 2 s so it doesn't compete
              // with PMREM generation + EffectComposer allocation that happen
              // during the same sceneLoaded window. Desktop doesn't notice the
              // delay; mobile gets breathing room during the critical startup.
              if (hdrEntries.length > 0) {
                setTimeout(() => {
                  if (!disposed) engine.preload(hdrEntries);
                }, 2000);
              }
            }
            // Turn lights on instantly (no fade) if the model has a light signature configured.
            // Deferred via queueMicrotask because the engine calls lightSignatureManager.init()
            // AFTER emitting sceneLoaded — the manager's _initialized flag would still be false
            // if we called setLightSignature() synchronously here.
            if (config.model.features.lightSignature && config.model.lightSignature) {
              queueMicrotask(() => {
                if (!disposed) engine.setLightSignature(true, false);
              });
            }
            // Enable dimensions overlay if the model has the measures feature flag.
            // The 3D ground icon + car-click handler are wired inside the engine —
            // calling enableDimensions() is all the frontend needs to do.
            if (config.features.measures) {
              queueMicrotask(() => {
                if (!disposed) engine.enableDimensions();
              });
            }
            // Register rim state in the store so the UI knows the active selection.
            // For base-node (nodeName) rims the engine already shows them via setModelScene,
            // so no showAsset() call is needed. For URL-based default rims (no nodeName),
            // we must call showAsset() explicitly so the external GLB is mounted on load.
            if (config.features.rimSelector && config.model.rims) {
              const { defaultId, sets } = config.model.rims;
              setSelectedRim(defaultId);
              const defaultSet = sets?.find((s) => s.id === defaultId);
              if (defaultSet?.url) {
                const rafId = requestAnimationFrame(() => {
                  if (!disposed) {
                    engine.showAsset(defaultId, { animated: false });
                  }
                });
                unsubs.push(() => cancelAnimationFrame(rafId));
              }
            }
            // Animate smoothly from current position to 100% over ~500ms
            // instead of jumping — 20ms ticks of +3% each.
            let fillProg = simProgress;
            const fillInterval = setInterval(() => {
              fillProg = Math.min(100, fillProg + 3);
              setLoadingProgress(Math.round(fillProg));
              if (fillProg >= 100) clearInterval(fillInterval);
            }, 20);
            unsubs.push(() => clearInterval(fillInterval));
            // Delay reveal to allow HDR env-map + first full frame to render.
            const t = setTimeout(() => {
              if (!disposed) setSceneReady(true);
            }, 600);
            unsubs.push(() => clearTimeout(t));
          }),
          engine.on('error', ({ code }) => {
            clearInterval(simInterval);
            setError({ code, recoverable: !FATAL_ENGINE_ERRORS.has(code) });
          }),
          () => clearInterval(simInterval),
        );
      })
      .catch((err) => {
        console.error('[IMS] createEngine failed:', err);
        if (!disposed) {
          setError({ code: 'ENGINE_CRASH', recoverable: true });
        }
      });

    return () => {
      disposed = true;
      unsubs.forEach((fn) => fn());
      engineRef.current?.dispose();
      engineRef.current = null;
    };
  }, [
    containerRef,
    config,
    setError,
    setLoadingProgress,
    setSceneReady,
    setActiveScene,
    setSelectedColor,
    setSelectedRim,
  ]);

  return engineRef;
}
